import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { CustomOverridesState, FieldConfig } from './types';

export const useCustomOverrides = create<CustomOverridesState>()(
  persist(
    (setState, getState) => ({
      fields: [],
      overrides: {},
      getIsActive: () =>
        Object.values(getState().overrides).filter((v) => v !== undefined)
          .length > 0,
      _registerField: (config: FieldConfig) =>
        setState((state) => {
          if (state.fields.some((f) => f.name === config.name)) return state;
          return { ...state, fields: [...state.fields, config] };
        }),
      override: (key, value) =>
        setState((state) => ({
          ...state,
          overrides: { ...state.overrides, [key]: value },
        })),
      clearOverride: (key) =>
        setState((state) => ({
          ...state,
          overrides: { ...state.overrides, [key]: undefined },
        })),
      clearAll: () => setState((state) => ({ ...state, overrides: {} })),
    }),
    {
      name: 'custom-overrides',
      partialize: (state) => ({ overrides: state.overrides }),
    }
  )
);

type OverrideOptions = {
  group?: string;
};

export const createBooleanOverride = <T extends string>(
  name: T,
  options?: OverrideOptions
): (() => boolean | undefined) => {
  useCustomOverrides
    .getState()
    ._registerField({ name, type: 'boolean', group: options?.group });
  return () =>
    useCustomOverrides((s) => s.overrides[name]) as boolean | undefined;
};

export const createDropdownOverride = <T>(
  name: string,
  dropdownOptions: T[],
  options?: OverrideOptions
): (() => T | undefined) => {
  useCustomOverrides.getState()._registerField({
    name,
    type: 'dropdown',
    options: dropdownOptions,
    group: options?.group,
  });
  return () => useCustomOverrides((s) => s.overrides[name]) as T | undefined;
};
